import { useState, type ReactNode } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { Globe, Link2, Lock } from "lucide-react";
import { toast } from "@strivelabs/ui-core";
import { useCopyToClipboard } from "@strivelabs/ui-blocks";
import {
  Button,
  Popover,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@strivelabs/ui-core/v2";
import { cn } from "@/lib/utils";
import { useShareLink } from "@/hooks/useShareLink";
import { useUserRole } from "@/hooks/useUserRole";
import { ANALYTICS_EVENTS, useTrack, type ShareSurface } from "@/lib/analytics";
import type { ShareEntityType } from "@/types/share";

export interface ShareLinkPopoverProps {
  entityType: ShareEntityType;
  entityId: string;
  /** Where the control sits, for analytics. */
  surface: ShareSurface;
  /** The control that opens the popover. It must accept `onClick`. */
  trigger: ReactNode;
  /** What the popover is titled with: the entity's own name. */
  title: string;
  /**
   * The app path that opens the entity for members, `/ui/artifacts/949`. It is
   * the link "Copy private link" copies while the entity is not public.
   */
  privatePath: string;
  /**
   * What the Export tab holds. The entity knows what it can be exported as,
   * so it brings this; without it the popover has no Export tab.
   */
  exportContent?: ReactNode;
}

type Access = "workspace" | "public";

/** The full address of a path, on the origin this app is served from. */
const absoluteUrl = (path: string): string => `${window.location.origin}${path}`;

/**
 * One place for everything that gets an entity out of the app: who can open
 * it (Share) and the files it can be saved as (Export), the way Claude and
 * ChatGPT gather them under one Share control.
 *
 * Share is "General access": only the workspace's members, or anyone with
 * the link. Choosing anyone creates the public link and choosing members only
 * revokes it, so there is no separate create or stop button to find. The
 * footer copies whichever link fits: the public one, or the member-only app
 * address. A public link pinned to an older version offers to catch up.
 *
 * Entity-agnostic on purpose. The link is read when the popover opens, so a
 * page with a Share control nobody touches costs no request. Room is left for
 * people and roles later, above "General access".
 *
 * Who may change access is decided here, for every kind, the way the server
 * decides it once for every share route: members can, guests cannot. A guest
 * gets Export only, and nothing renders until the membership has loaded.
 */
export function ShareLinkPopover({
  entityType,
  entityId,
  surface,
  trigger,
  title,
  privatePath,
  exportContent,
}: ShareLinkPopoverProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"share" | "export">("share");
  const { isGuest, isLoaded } = useUserRole();
  const canShare = !isGuest;
  const showsExport = exportContent !== undefined;

  // Not until the membership says who this is; and nothing this viewer can do
  // with the entity means no control at all, not a popover holding a title.
  if (!isLoaded || (!canShare && !showsExport)) {
    return null;
  }

  const sharePanel = canShare ? (
    <SharePanel
      entityType={entityType}
      entityId={entityId}
      surface={surface}
      privatePath={privatePath}
      enabled={open && (!showsExport || tab === "share")}
    />
  ) : null;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      side="bottom"
      align="end"
      role="dialog"
      aria-label={`Share ${title}`}
      contentClassName="w-[400px] max-w-[calc(100vw-32px)] p-0"
    >
      <div className="flex flex-col text-v2-sm text-v2-foreground">
        <p className="truncate px-5 pb-3 pt-4 text-v2-base font-medium">{title}</p>

        {sharePanel && showsExport ? (
          <Tabs value={tab} onValueChange={(value) => setTab(value as "share" | "export")} variant="segment" className="flex flex-col">
            <div className="px-5">
              <TabsList className="w-full">
                <TabsTrigger value="share">Share</TabsTrigger>
                <TabsTrigger value="export">Export</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="share">{sharePanel}</TabsContent>
            <TabsContent value="export">
              <div className="px-5 pb-5 pt-4">{exportContent}</div>
            </TabsContent>
          </Tabs>
        ) : (
          sharePanel ?? <div className="px-5 pb-5 pt-1">{exportContent}</div>
        )}
      </div>
    </Popover>
  );
}

interface SharePanelProps {
  entityType: ShareEntityType;
  entityId: string;
  surface: ShareSurface;
  privatePath: string;
  /** Read the link only while this panel is on screen. */
  enabled: boolean;
}

/** The Share tab: general access, the version a public link shows, and copy. */
function SharePanel({ entityType, entityId, surface, privatePath, enabled }: SharePanelProps) {
  const { organization } = useOrganization();
  const { query, share, revoke } = useShareLink(entityType, entityId, enabled);
  const trackEvent = useTrack();
  const link = query.data ?? null;
  const access: Access = link ? "public" : "workspace";
  const busy = share.isPending || revoke.isPending;
  const workspace = organization?.name ?? "your workspace";

  const failed = (message: string) => {
    toast({ title: message, description: "Try again in a moment.", variant: "destructive" });
  };

  const choose = (next: Access) => {
    if (next === access || busy) return;
    if (next === "public") {
      share.mutate(undefined, {
        onSuccess: () => trackEvent(ANALYTICS_EVENTS.SHARE_LINK_CREATED, { entity_type: entityType, surface }),
        onError: () => failed("Couldn't create the link"),
      });
      return;
    }
    revoke.mutate(undefined, {
      onSuccess: () => trackEvent(ANALYTICS_EVENTS.SHARE_LINK_REVOKED, { entity_type: entityType, surface }),
      onError: () => failed("Couldn't stop sharing"),
    });
  };

  const catchUp = (pinned: number, latest: number) => {
    share.mutate(latest, {
      onSuccess: () => trackEvent(ANALYTICS_EVENTS.SHARE_LINK_UPDATED, {
        entity_type: entityType,
        surface,
        versions_behind: latest - pinned,
      }),
      onError: () => failed("Couldn't update the link"),
    });
  };

  const { copy } = useCopyToClipboard({
    onSuccess: () => {
      toast({ title: "Link copied" });
      trackEvent(ANALYTICS_EVENTS.SHARE_LINK_COPIED, {
        entity_type: entityType,
        surface,
        link: link ? "public" : "private",
      });
    },
    onError: () => failed("Couldn't copy the link"),
  });

  if (query.isError) {
    return (
      <p className="px-5 pb-5 pt-4 text-v2-xs text-v2-muted-foreground" role="alert">
        Couldn't check who can open this. Close and try again.
      </p>
    );
  }

  const loading = query.isLoading;

  return (
    <>
      <div className="px-5 pb-4 pt-4">
        <p className="mb-2 text-v2-xs text-v2-muted-foreground">General access</p>
        <div role="radiogroup" aria-label="General access" className="flex flex-col gap-1">
          <AccessOption
            icon={<Lock />}
            label={`Only people in ${workspace}`}
            hint="Members sign in to open it."
            selected={!loading && access === "workspace"}
            disabled={loading || busy}
            onSelect={() => choose("workspace")}
          />
          <AccessOption
            icon={<Globe />}
            label="Anyone with the link"
            hint="No sign-in needed. They can't edit it or see your chats."
            selected={!loading && access === "public"}
            disabled={loading || busy}
            onSelect={() => choose("public")}
          />
        </div>

        {link && link.version !== null && link.latest_version !== null ? (
          <VersionLine
            pinned={link.version}
            latest={link.latest_version}
            busy={busy}
            onCatchUp={catchUp}
          />
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-v2-border px-5 py-3">
        <p className="min-w-0 text-v2-xs text-v2-muted-foreground" role="status">
          {loading
            ? "Checking who can open it…"
            : link
              ? "Anyone with this link can view it."
              : `Only members of ${workspace} can open this link.`}
        </p>
        <Button
          variant="secondary"
          size="sm"
          icon={link ? <Link2 /> : <Lock />}
          onClick={() => void copy(absoluteUrl(link ? link.path : privatePath))}
          disabled={loading || busy}
          className="shrink-0"
        >
          {link ? "Copy link" : "Copy private link"}
        </Button>
      </div>
    </>
  );
}

interface AccessOptionProps {
  icon: ReactNode;
  label: string;
  hint: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

/** One choice of who can open the entity, as a radio row. */
function AccessOption({ icon, label, hint, selected, disabled, onSelect }: AccessOptionProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-foreground/20",
        "disabled:cursor-default",
        selected
          ? "border-v2-foreground/25 bg-v2-card"
          : "border-transparent hover:bg-v2-card/60",
      )}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-v2-muted text-v2-foreground [&_svg]:size-4">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-v2-sm font-medium">{label}</span>
        <span className="block text-v2-xs text-v2-muted-foreground">{hint}</span>
      </span>
      <span
        aria-hidden
        className={cn(
          "size-4 shrink-0 rounded-full border",
          selected ? "border-[5px] border-v2-foreground" : "border-v2-border",
        )}
      />
    </button>
  );
}

/** Which version a public link shows, with a way to catch it up. */
function VersionLine({ pinned, latest, busy, onCatchUp }: {
  pinned: number;
  latest: number;
  busy: boolean;
  onCatchUp: (pinned: number, latest: number) => void;
}) {
  const behind = pinned < latest;

  return (
    <div className="mt-3 flex items-center justify-between gap-3 text-v2-xs text-v2-muted-foreground">
      <span>
        {behind ? `Shows version ${pinned} · current is ${latest}` : `Shows the current version (v${pinned})`}
      </span>
      {behind ? (
        <Button variant="borderless" size="xs" onClick={() => onCatchUp(pinned, latest)} disabled={busy}>
          Update to v{latest}
        </Button>
      ) : null}
    </div>
  );
}
